import { FormControl, IconButton, Select, Typography } from "@mui/material";
import Box from "../../../components-dev/BoxExtended";
import { getSizing } from "../../../sizing";
import XNGToggle from "../../../low-level/button_toggle";
import XNGClose from "../../../low-level/button_close";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import { useState } from "react";
import {
  NotificationResponseType,
  RespondToRequestForAccessToPost,
  ServiceProviderType,
} from "../../../../profile-sdk";
import { API_CLIENTS_BACKEND, API_USERS } from "../../../../api/api";
import { PatchClientDistrictAccessRequestBody, ServiceProviderType as XngBackendServiceProviderType } from "@xng/xng-backend";
import { useXNGSelector } from "../../../../context/store";
import { selectClientID } from "../../../../context/slices/loggedInClientSlice";
import { selectStateInUS } from "../../../../context/slices/stateInUsSlice";
import {
  selectLoggedInClientAssignment,
  selectUser,
} from "../../../../context/slices/userProfileSlice";
import XNGSelect from "../../../low-level/dropdown";
import { NotificationItem } from "../notifications/NotificationItem";
import { ApproveDistrictAccessModal } from "../notifications/ApproveDistrictAccessModal";
import { getBitsFromRoleLabels, bitsToRoleAssignments } from "../../../../utils/xlogs_role_mapper";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MSBICONS } from "../../../../fortitude";
import type {
  GetNotificationsDistrictAccessRequestNotification,
  GetNotificationsProxyAccessRequestNotification,
  GetNotificationsResponseNotification,
} from "@xng/xng-backend";
import { API_NOTIFICATIONS } from "../../../../api/api";
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);

enum NotificationType {
  ProxyAccessRequest = 1,
  SupervisorAccessRequest,
  DistrictAccessRequest,
  ProxyAccessResponse,
  SupervisorAccessResponse,
  DistrictAccessResponse,
  ServiceProviderNotification,
  ServiceProviderDistrictAssignmentsUpdate
}

interface INotificationsMenu {
  districtAccessRequestNotifications?: GetNotificationsDistrictAccessRequestNotification[] | null;
  proxyAccessRequestNotifications?: GetNotificationsProxyAccessRequestNotification[] | null;
  responseNotifications?: GetNotificationsResponseNotification[] | null;
  notificationsIsLoading: boolean;
  onClose: () => void;
  fetchAndSetNotifications: () => Promise<void>;
}

function NotificationsMenu(props: INotificationsMenu) {
  const [showOnlyUnread, setShowOnlyUnread] = useState<boolean>(false);

  return (
    <Box sx={{ width: getSizing(50) }}>
      <Header
        showOnlyUnread={showOnlyUnread}
        onToggleShowOnlyUnread={() => setShowOnlyUnread(!showOnlyUnread)}
        onClose={() => props.onClose()}
        onRefresh={props.fetchAndSetNotifications}
        notificationsIsLoading={props.notificationsIsLoading}
      />
      <Content
        showOnlyUnread={showOnlyUnread}
        fetchAndSetNotifications={props.fetchAndSetNotifications}
        proxyAccessRequestNotifications={props.proxyAccessRequestNotifications}
        districtAccessRequestNotifications={props.districtAccessRequestNotifications}
        responseNotifications={props.responseNotifications}
      />
    </Box>
  );
}

interface IHeader {
  onToggleShowOnlyUnread: () => void;
  showOnlyUnread: boolean;
  notificationsIsLoading: boolean;
  onClose: () => void;
  onRefresh: () => void;
}
function Header(props: IHeader) {
  const { LoadingAnimation } = MSBICONS;
  return (
    <Box
      name="Header"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: getSizing(2),
        paddingRight: getSizing(1),
        paddingY: getSizing(0.5),
      }}
    >
      <Box name="Header | LEFT" sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h6" className="noselect">
          Notifications
        </Typography>
        <IconButton size="small" onClick={props.onRefresh} disabled={props.notificationsIsLoading}>
          {props.notificationsIsLoading ? (
            <LoadingAnimation width={"20px"} height={"20px"} />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Box name="Header | RIGHT" sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body2" className="noselect">
          Show Only Unread
        </Typography>
        <XNGToggle value={props.showOnlyUnread} onToggle={() => props.onToggleShowOnlyUnread()} />
        <XNGClose onClick={() => props.onClose()} size="modal" />
      </Box>
    </Box>
  );
}

function Content(props: {
  showOnlyUnread: boolean;
  fetchAndSetNotifications: () => Promise<void>;
  proxyAccessRequestNotifications?: GetNotificationsProxyAccessRequestNotification[] | null;
  districtAccessRequestNotifications?: GetNotificationsDistrictAccessRequestNotification[] | null;
  responseNotifications?: GetNotificationsResponseNotification[] | null;
}) {
  // SELECTORS
  const loggedInClientId = useXNGSelector(selectClientID);
  const state = useXNGSelector(selectStateInUS);
  const loggedInClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const serviceProviderOfUser = loggedInClientAssignment.serviceProviderProfile;
  const userProfile = useXNGSelector(selectUser);

  // STATE
  const [showApproveDistrictAccessModal, setShowApproveDistrictAccessModal] =
    useState<boolean>(false);
  const [selectedDistrictAccessRequest, setSelectedDistrictAccessRequest] =
    useState<GetNotificationsDistrictAccessRequestNotification | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    proxyAccessRequestNotifications,
    districtAccessRequestNotifications,
    responseNotifications,
  } = props;

  async function responseYesToProxyAccessRequest(
    notification: GetNotificationsProxyAccessRequestNotification,
  ) {
    const request: RespondToRequestForAccessToPost = {
      notificationId: notification.notificationRefId!,
      message: `${serviceProviderOfUser?.firstName} ${serviceProviderOfUser?.lastName} has allowed access to post in their account.`,
      responseType: NotificationResponseType.NUMBER_1,
      //HACK: The sdk needs to be updated to take in the requestedServiceProviderId, and RespondingServiceProvider, but for now the
      // requestingUserId, and the RespondingUser fields of the request are used as if they're the service provider, so we'll incorrectly
      // fill the user fields with the user's serviceProvider info.
      //"207be8f0-6343-492d-9c84-0fef51b635d4"
      requestedUserId: notification.requestingUser?.id,
      respondingUser: { ...serviceProviderOfUser, hasGrantedAccessToPost: true },
    };
    await API_USERS.v1UsersRequestProxyAccessToPostRespondPost(loggedInClientId!, state, request);
  }

  async function onYesClickOfProxyAccessRequest(
    notification: GetNotificationsProxyAccessRequestNotification,
  ) {
    await responseYesToProxyAccessRequest(notification);
    await markNotificationAsRead(notification.notificationRefId!);
    await props.fetchAndSetNotifications();
    setIsLoading(false);
  }

  async function responseNoToProxyAccessRequest(notificationId: string) {
    const request: RespondToRequestForAccessToPost = {
      notificationId: notificationId,
      message: `${serviceProviderOfUser?.firstName} ${serviceProviderOfUser?.lastName} has denied your access to post in their account.
      You may attempt to request access again at any time.`,
      responseType: NotificationResponseType.NUMBER_0,
      //HACK: The sdk needs to be updated to take in the requestedServiceProviderId, and RespondingServiceProvider, but for now the
      // requestingUserId, and the RespondingUser fields of the request are used as if they're the service provider, so we'll incorrectly
      // fill the user fields with the user's serviceProvider info.
      requestedUserId: serviceProviderOfUser?.id,
      respondingUser: serviceProviderOfUser,
    };
    await API_USERS.v1UsersRequestProxyAccessToPostRespondPost(loggedInClientId!, state, request);
  }

  async function onNoClickOfProxyAccessRequest(
    notification: GetNotificationsProxyAccessRequestNotification,
  ) {
    await responseNoToProxyAccessRequest(notification.notificationRefId!);
    await markNotificationAsRead(notification.notificationRefId!);
    await props.fetchAndSetNotifications();
    setIsLoading(false);
  }

  async function onYesClickOfDistrictAccessRequest(
    notification: GetNotificationsDistrictAccessRequestNotification,
  ) {
    setSelectedDistrictAccessRequest(notification);
    setShowApproveDistrictAccessModal(true);
    await markNotificationAsRead(notification.notificationRefId!);
    await props.fetchAndSetNotifications();
  }

  async function respondYesToDistrictAccessRequest(
    notification: GetNotificationsDistrictAccessRequestNotification,
    roleLabels: string[],
    selectedServiceProviders: ServiceProviderType[],
  ) {
    const roleBits = getBitsFromRoleLabels(roleLabels);
    const body: PatchClientDistrictAccessRequestBody = {
      responseType: NotificationResponseType.NUMBER_1 as unknown as PatchClientDistrictAccessRequestBody["responseType"],
      roleAssignments: bitsToRoleAssignments(roleBits) as unknown as PatchClientDistrictAccessRequestBody["roleAssignments"],
      roles: roleBits as unknown as PatchClientDistrictAccessRequestBody["roles"],
      serviceProviderTypesAssignments: selectedServiceProviders as XngBackendServiceProviderType[],
    };
    await API_CLIENTS_BACKEND.patchClientDistrictAccessRequest({
      clientId: loggedInClientId!,
      notificationId: notification.notificationRefId!,
      patchClientDistrictAccessRequestBody: body,
    });
  }

  async function respondNoToDistrictAccessRequest(
    notification: GetNotificationsDistrictAccessRequestNotification,
    roleLabels: string[],
    selectedServiceProviders: ServiceProviderType[],
  ) {
    const roleBits = getBitsFromRoleLabels(roleLabels);
    const body: PatchClientDistrictAccessRequestBody = {
      responseType: NotificationResponseType.NUMBER_0 as unknown as PatchClientDistrictAccessRequestBody["responseType"],
      roleAssignments: bitsToRoleAssignments(roleBits) as unknown as PatchClientDistrictAccessRequestBody["roleAssignments"],
      roles: roleBits as unknown as PatchClientDistrictAccessRequestBody["roles"],
      serviceProviderTypesAssignments: selectedServiceProviders as XngBackendServiceProviderType[],
    };
    await API_CLIENTS_BACKEND.patchClientDistrictAccessRequest({
      clientId: loggedInClientId!,
      notificationId: notification.notificationRefId!,
      patchClientDistrictAccessRequestBody: body,
    });
  }

  async function onNoClickOfDistrictAccessRequest(
    notification: GetNotificationsDistrictAccessRequestNotification,
  ) {
    await respondNoToDistrictAccessRequest(notification, ["Autonomous"], []);
    await markNotificationAsRead(notification.notificationRefId!);
    await props.fetchAndSetNotifications();
  }

  async function markNotificationAsRead(notificationId: string) {
    // await API_USERS.v1UsersNotificationsNotificationIdReadPatch(notificationId, partionKey, state);
    await API_NOTIFICATIONS.patchNotificationRead(
      { notificationId: notificationId },
      { headers: { "Content-Type": "application/json" } },
    );
    await props.fetchAndSetNotifications();
  }
  // RENDERER SWITCH CASE
  function renderNotificationItem(
    n:
      | GetNotificationsProxyAccessRequestNotification
      | GetNotificationsDistrictAccessRequestNotification
      | GetNotificationsResponseNotification,
    i: number,
  ): JSX.Element {
    switch (n.typeId) {
      case NotificationType.DistrictAccessRequest: // District Access Request
        // console.log("Rendering District Access Request Notification: ", n);
        return (
          <NotificationItem
            key={i}
            text={
              "requestingUser" in n && n.requestingUser
                ? `${n.requestingUser.firstName} ${
                    n.requestingUser.lastName 
                  } (${n.requestingUser.email}) is requesting access to ${
                    "targetDistrict" in n && n.targetDistrict ? n.targetDistrict : ""
                  }.`
                : ""
            }
            unread={n.isRead === undefined || n.isRead === null}
            onYes={() => onYesClickOfDistrictAccessRequest(n)}
            onNo={() => {
              onNoClickOfDistrictAccessRequest(n);
              setIsLoading(true);
            }}
            onRead={() => markNotificationAsRead(n.notificationRefId!)}
            requestingUser={
              "requestingUser" in n && n.requestingUser
                ? `${n.requestingUser.firstName} ${n.requestingUser.lastName}`
                : ""
            }
            isLoading={isLoading}
          />
        );
      case NotificationType.ProxyAccessRequest: // Proxy Access to Post Request
        return (
          <NotificationItem
            key={i}
            text={
              "requestingUser" in n && n.requestingUser
                ? `${n.requestingUser.firstName} ${n.requestingUser.lastName} (${n.requestingUser.email}) is requesting access to post in your account.`
                : ""
            }
            unread={n.isRead === undefined || n.isRead === null}
            onYes={() => {
              onYesClickOfProxyAccessRequest(n);
              setIsLoading(true);
            }}
            onNo={() => {
              onNoClickOfProxyAccessRequest(n);
              setIsLoading(true);
            }}
            onRead={() => markNotificationAsRead(n.notificationRefId!)}
            isLoading={isLoading}
            requestingUser={
              "requestingUser" in n && n.requestingUser
                ? `${n.requestingUser.firstName} ${n.requestingUser.lastName}`
                : ""
            }
          />
        );
      case NotificationType.DistrictAccessResponse || NotificationType.ProxyAccessResponse: // Proxy Access Response
        // console.log("Rendering Response Notification: ", n);
        return (
          <NotificationItem
            key={i}
            text={
              "respondingUser" in n && n.respondingUser
                ? `${n.respondingUser.firstName} ${n.respondingUser.lastName} (${n.respondingUser.email}) has ${
                    n.status ? "granted" : "denied"
                  } you access.`
                : ""
            }
            unread={n.isRead === undefined || n.isRead === null}
            onRead={() => markNotificationAsRead(n.notificationRefId!)}
          />
        );
      default:
        return <></>;
      // return (
      //   <NotificationItem
      //     key={i}
      //     text={n.message ?? ""}
      //     unread={n.read === undefined || n.read === null}
      //     onRead={() => markNotificationAsRead(n.id, n.partitionKey, state)}
      //   />
      // );
    }
  }

  // NOTIFICATION ARRAYS (Today, Yesterday, Older)
  const TWO_DAYS_AGO = dayjs().subtract(2, "days");
  // console.log('notifications', props.notifications)
  const filteredDistrictAccessRequests = districtAccessRequestNotifications
    ?.filter((n: GetNotificationsDistrictAccessRequestNotification) =>
      props.showOnlyUnread ? n.isRead === undefined || n.isRead === null : true,
    )
    .reverse();

  const filteredproxyAccessRequests = proxyAccessRequestNotifications
    ?.filter((n: GetNotificationsProxyAccessRequestNotification) =>
      props.showOnlyUnread ? n.isRead === undefined || n.isRead === null : true,
    )
    .reverse();

  const filteredResponseNotifications = responseNotifications
    ?.filter((n: GetNotificationsResponseNotification) =>
      props.showOnlyUnread ? n.isRead === undefined || n.isRead === null : true,
    )
    .reverse();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingBottom: getSizing(2),
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        {filteredResponseNotifications?.map((n: GetNotificationsResponseNotification, i) =>
          renderNotificationItem(n, i),
        )}
        <Typography
          display={filteredDistrictAccessRequests?.length! > 0 ? "inline-block" : "none"}
          sx={{ marginTop: getSizing(1), marginLeft: getSizing(2) }}
          variant="overline"
          className="noselect"
        >
          District Access Requests
        </Typography>
        {filteredDistrictAccessRequests?.map(
          (n: GetNotificationsDistrictAccessRequestNotification, i) => renderNotificationItem(n, i),
        )}
        <Typography
          display={filteredproxyAccessRequests?.length! > 0 ? "inline-block" : "none"}
          sx={{ marginTop: getSizing(1), marginLeft: getSizing(2) }}
          variant="overline"
          className="noselect"
        >
          Proxy User Requests
        </Typography>
        {filteredproxyAccessRequests?.map((n: GetNotificationsProxyAccessRequestNotification, i) =>
          renderNotificationItem(n, i),
        )}
      </Box>
      <ApproveDistrictAccessModal
        stateInUs={state}
        serviceProviderTypes={userProfile?.serviceProviderTypes || []}
        handleDone={(roleLabels: string[], selectedServiceProviders: ServiceProviderType[]) => {
          respondYesToDistrictAccessRequest(
            selectedDistrictAccessRequest!,
            roleLabels,
            selectedServiceProviders,
          );
          props.fetchAndSetNotifications();
        }}
        districtAccessRequest={selectedDistrictAccessRequest}
        setShowApproveDistrictAccessModal={(show: boolean) =>
          setShowApproveDistrictAccessModal(show)
        }
        showApproveDistrictAccessModal={showApproveDistrictAccessModal}
      />
    </>
  );
}

export default NotificationsMenu;
